import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, MapPin } from "lucide-react";
import volleyballApi from "@/services/api";

export const RecentMatches = () => {
  const { championshipId } = useParams();
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchMatches = async () => {
      if (!championshipId) return;
      
      try {
        const matches = await volleyballApi.getChampionshipMatches(championshipId);
        // Get last 6 played matches
        const played = matches.filter(m => m.played).slice(-6).reverse();
        setRecentMatches(played);
      } catch (error) {
        console.error("Error loading matches:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMatches();
  }, [championshipId]);
  
  if (loading) {
    return (
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground">Caricamento...</p>
        </div>
      </section>
    );
  }
  
  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-2">Ultimi Risultati</h2>
            <p className="text-muted-foreground">Le partite più recenti del campionato</p>
          </div>
          <Link to={`/championship/${championshipId}/calendario`}>
            <Button variant="outline" className="gap-2">
              Vedi Tutto
              <ChevronRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recentMatches.map((match) => (
            <Link key={match.id} to={`/championship/${championshipId}/partita/${match.id}`}>
              <Card className="p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <Badge variant="secondary" className="text-xs">
                    Giornata {match.round}
                  </Badge>
                  {match.played && (
                    <Badge variant="outline" className="text-xs text-success border-success/50">
                      Conclusa
                    </Badge>
                  )}
                </div>
                
                {/* Teams and Score */}
                <div className="space-y-3 mb-4 flex-grow">
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${
                      match.winner === match.homeTeam ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {match.homeTeam}
                    </span>
                    {match.played && <span className="text-2xl font-bold">{match.homeScore}</span>}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${
                      match.winner === match.awayTeam ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {match.awayTeam}
                    </span>
                    {match.played && <span className="text-2xl font-bold">{match.awayScore}</span>}
                  </div>
                </div>
                
                {/* Date and Location */}
                <div className="pt-4 border-t border-border space-y-2 text-sm text-muted-foreground mt-auto">
                  <div>{match.date} - {match.time}</div>
                  {match.venue && (
                    <div className="flex items-start gap-1">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{match.venue}</span>
                    </div>
                  )}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
